import { Fragment, type CSSProperties } from 'react';
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { When } from 'react-if';
import { Tooltip } from '@mui/material';
import { EllipsisVerticalIcon } from '@heroicons/react/24/solid';

import { classNames }  from '../lib/classNames';

import { SmallSpinner } from './SmallSpinner';
import { Badge } from './Badge';
import { Button, ButtonColorOpts } from './Button';

const themes = {
  white: {
    items: {
      active: `bg-gray-100 text-gray-900`,
      inactive: `text-gray-700`,
    },
  },
  primary: {
    items: {
      active: `bg-gray-100 text-paidniceGreen-900`,
      inactive: `text-paidniceGreen-700`,
    },
  },
  outline: {
    items: {
      active: `bg-gray-100 text-paidniceGreen-900`,
      inactive: `text-paidniceGreen-700`,
    },
  },
  clear: {
    items: {
      active: `bg-gray-100 text-gray-900`,
      inactive: `text-gray-700`,
    },
  },
};

export type IDropDownTheme = keyof typeof themes;

export interface IOption {
  paywalled?: boolean;
  badge?: string;
  label: string;
  badgeColor?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick?: (...args: any) => void;
  disabled?: boolean;
  loading?: boolean;
  tooltip?: string;
  value?: string | number | boolean | null;
  style?: CSSProperties;
}

interface IDropdownProps {
  options: Array<Array<IOption>>;
  label?: string;
  className?: string;
  disabled?: boolean;
  theme?: IDropDownTheme;
  buttonColor?: ButtonColorOpts;
  position?: `left` | `right`;
  onSelect?: (option: IOption) => void;
  size?: `sm` | `md` | `lg`;
  ellipsis?: boolean;
  loading?: boolean;
}

export type DropdownOption = IOption;

export function Dropdown({
  options, // Multidimensional array of options
  label,
  className,
  disabled,
  theme = `white`,
  buttonColor,
  position = `left`,
  onSelect,
  size = `sm`,
  ellipsis,
  loading,
}: IDropdownProps) {

  function onClick(e, option, close) {
    e.preventDefault();

    if (option.paywalled || option.disabled) return;

    if (onSelect) {
      // Global onSelect handler takes priority
      onSelect(option);

      close();

      return;
    }

    option.onClick?.(option);

    close();
  }

  function renderMenuItems() {
    return options.map((optionArray, i) => {
      return (
        <div className={ classNames(
          size === `sm` && `py-0`,
          size === `md` && `py-1`,
          size === `lg` && `py-2`,
        ) }
        key={ i }>
          {
            optionArray.map((option, j) => {
              return (
                <MenuItem
                  key={ j }
                >
                  { ({ focus, close }) => (
                    <Tooltip
                      title={ option.tooltip }
                      placement={ `left` }
                    >
                      <a
                        href={ `#` }
                        onClick={ e => onClick(e, option, close) }
                        style={ option.style }
                        className={ classNames(
                          focus ? themes[theme].items.active : themes[theme].items.inactive,
                          size === `sm` && `px-3 py-1.5 text-xs`,
                          size === `md` && `px-4 py-2 text-sm`,
                          size === `lg` && `px-4 py-2 text-sm`,
                          `block`,
                          option.paywalled || option.disabled && `opacity-50 cursor-not-allowed`,
                        ) }
                      >
                        { option.label }
                        <When condition={ option.paywalled }>
                          { ` ` }
                          <Badge color={ `gray` }
                            message={ `Upgrade Plan` } />
                        </When>
                        <When condition={ option.badge }>
                          { ` ` }
                          <Badge color={ option.badgeColor }
                            message={ option.badge } />
                        </When>
                      </a>
                    </Tooltip>
                  ) }
                </MenuItem>
              );
            })
          }
        </div>
      );
    });
  }

  const anyLoading = options.some(optionArray => optionArray.some(option => option.loading));

  return (
    <Menu as={ `div` }
      className={
        classNames(
          `relative inline-block text-left`,
          className,
        )
      }
    >
      <div>
        <MenuButton
          as={ (props => <Button
            size={ size }
            { ...props }
            className={ classNames(
              props.className,
              ellipsis && `rounded-full`,
              theme === `clear` && `bg-transparent text-gray-700 hover:text-gray-800 hover:bg-transparent`,
            ) }
            color={ theme === `outline` ? `paidniceGreenOutline` : buttonColor }
          />) as unknown as `button` }
          disabled={ disabled || loading }
          className={ classNames(
            ellipsis && `rounded-full`,
            loading && `cursor-wait`,
            size === `sm` ? `py-0.5 max-h-5` : ``,
          ) }
        >
          <When condition={ ellipsis }>
            <EllipsisVerticalIcon className={ `w-6 h-6 text-white` } />
          </When>

          <When condition={ !ellipsis }>
            { label }
          </When>
          <When condition={ anyLoading }>
            <SmallSpinner className={ `ml-2` } />
          </When>

          <When condition={ !ellipsis }>
            <ChevronDownIcon className={ classNames(
              `-mr-1 text-gray-400`,
              size === `sm` ? `w-4 h-4` : `h-5 w-5`,
              buttonColor && buttonColor !== `white` && `text-white`,
            ) }
            aria-hidden={ `true` }
            />
          </When>
        </MenuButton>
      </div>

      <Transition
        as={ Fragment }
        enter={ `transition ease-out duration-100` }
        enterFrom={ `transform opacity-0 scale-95` }
        enterTo={ `transform opacity-100 scale-100` }
        leave={ `transition ease-in duration-75` }
        leaveFrom={ `transform opacity-100 scale-100` }
        leaveTo={ `transform opacity-0 scale-95` }
      >
        <MenuItems className={
          classNames(
            `absolute`,
            position === `left` ? `right-0` : `left-0`,
            `z-10 mt-2 origin-top-right divide-y divide-gray-100 rounded-md whitespace-nowrap bg-white shadow-lg ring-1 ring-black/5 focus:outline-hidden`,
            // Overflow/scroll
            `overflow-auto max-h-60`,
          )
        }
        >
          {
            renderMenuItems()
          }
        </MenuItems>
      </Transition>
    </Menu>
  );
}
